import { ActiveReportStore, EmbeddedPowerBiStore } from "../stores";
import { Box, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { Contact } from "../../../../../customer-experience/src/lib/ShiOneClient";
import { EmbeddedPowerBIParams } from "../entities";
import { PbiContext } from "./PbiContext";
import { PowerBiReportStyles } from "../styles";
import { useObserveElementSize } from "../../../utils/useObserveElementSize";
import { useParams } from "react-router-dom";
import { usePowerBiReportCenter } from "../hooks";
import EmbeddedPowerBI from "./EmbeddedPowerBI";
import IdcsChangeRequestButton from "./report-app-bar/IdcsChangeRequestButton";
import PageHeader from "../../../global-layout/PageHeader";
import PowerBiDateRanges from "./report-app-bar/PowerBiDateRanges";
import PowerBiReportMenu from "./PowerBiReportMenu";
import React, { useMemo } from "react";
import ViewBookmarks from "./report-app-bar/ViewBookmarks";

export interface PbiBookmarksProps {
  useCurrentUser: unknown;
  useVirtualAdmin: unknown;
  getBookmarks: unknown;
  postBookmarks: unknown;
  putBookmarks: unknown;
  deleteBookmarks: unknown;
}

export interface PbiWebWorkerProps {
  webWorkerGetNewAccessToken: unknown;
}

export interface PowerBiReportCenterProps {
  header: string | string[];
  title?: string;
  reports: EmbeddedPowerBIParams[];
  showTimeRange?: boolean;
  showHeader?: boolean;
  overrideTheme?: boolean;
  hideFilters?: boolean;
  expandFilterPane?: boolean;
  children?: React.ReactNode;
  usePbiHook: any;
  webWorkerProps: PbiWebWorkerProps;
  pbiBookmarksProps: PbiBookmarksProps;
  /** Overrides the header back link, which defaults to the CX report center. */
  backButtonUrl?: string;
}

const getReportContainerHeight = (width: number) => {
  const windowHeight = window.innerHeight;
  if (width) {
    if (windowHeight > width * (8.8 / 16) + 200) {
      return `${width * (8.8 / 16)}px`;
    }
    return windowHeight - 200;
  }
  return "auto";
};

export function PowerBiReportCenter({
  header = "",
  reports = [],
  showTimeRange = true,
  showHeader = true,
  overrideTheme = false,
  hideFilters = false,
  children = <></>,
  usePbiHook,
  webWorkerProps,
  pbiBookmarksProps,
  backButtonUrl: backButtonUrlOverride,
}: Readonly<PowerBiReportCenterProps>) {
  const theme = useTheme();
  const styles = PowerBiReportStyles(theme);
  const { prefix } = useParams<{ prefix?: string }>();
  const { MoveReportDown } = usePowerBiReportCenter({
    reports,
    showTimeRange,
    showHeader,
    overrideTheme,
    hideFilters,
  });
  const [showIntervals] = EmbeddedPowerBiStore((s) => [s.showIntervals]);
  const ActiveReportParams = ActiveReportStore((s) => s.activeReportParams);
  const { measuredRef, width } = useObserveElementSize();
  const reportContainerHeight = getReportContainerHeight(width);
  const backButtonUrl =
    backButtonUrlOverride ??
    (prefix ? `/${prefix}/report-center` : "/report-center");

  const memoizedValue = useMemo(() => {
    return { usePbiHook, webWorkerProps, pbiBookmarksProps };
  }, [usePbiHook, webWorkerProps, pbiBookmarksProps]);

  const hideBookmarks = ActiveReportParams?.hideBookmarks ?? false;
  // @ts-ignore
  const accountId = (pbiBookmarksProps.useCurrentUser() as Contact)?.accountID;
  const hasIdcsAccess = accountId === 31976; //only this customer has access to IDCS

  return (
    <PbiContext.Provider value={memoizedValue}>
      <Box sx={styles.root}>
        <Box sx={{ paddingBottom: "1rem" }}>
          <PageHeader
            header={header}
            backButtonUrl={backButtonUrl}
            headerComponents={
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <Box
                  sx={{ display: { xs: "none", sm: "block" }, fontSize: "xs" }}
                >
                  {children}
                </Box>
                <Box
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    gap: 1.5,
                    fontSize: "xs",
                  }}
                >
                  {showIntervals && !ActiveReportParams?.hideIntervals && (
                    <PowerBiDateRanges />
                  )}
                  {hasIdcsAccess && <IdcsChangeRequestButton />}
                  {!hideBookmarks && <ViewBookmarks />}
                </Box>
              </Box>
            }
          />
        </Box>
        <Grid
          container
          justifyContent="flex-end"
          flexWrap={"nowrap"}
          sx={[
            styles.whiteBackground,
            MoveReportDown && { display: "none" },
            {
              height: reportContainerHeight,
            },
          ]}
        >
          <PowerBiReportMenu reports={reports} />
          <Grid sx={{ flexGrow: 1, minWidth: 0 }}>
            <EmbeddedPowerBI ref={measuredRef} />
          </Grid>
        </Grid>
      </Box>
    </PbiContext.Provider>
  );
}
